import type { NextMatchData } from '../../features/home/components/next-match-banner/next-match.model';
import { computed, Injectable, signal } from '@angular/core';
import { ticketEntriesPerUnit, ticketLineTotalCents } from '../utils/ticket-cart-pricing';
import { TicketCartItem } from '../components/sector-selection/sector-selection.model';

export interface MatchInfo {
    name: string;
    date: string;
    stadium: string;
    presentation?: NextMatchData;
}

const DEFAULT_TICKET_ITEM: TicketCartItem = {
    sector: { id: 'norte', nombre: 'NORTE', precio: 15, color: '#c6121e' },
    tarifa: { id: 'general', nombre: 'General', precio: 0 },
    cantidad: 1,
};

export interface TicketNominee {
    firstName: string;
    lastName: string;
    documentNumber: string;
    conadisNumber: string;
}
export interface TicketAttendee {
    key: string;
    ordinal: number;
    entryIndex: number;
    item: TicketCartItem;
    value: TicketNominee;
    saved: boolean;
}
export interface TicketReservation {
    id: string;
    expiresAt?: string;
    items: Array<{ id: string; eventSectorTicketTypeId: number; quantity: number }>;
}
const EMPTY_NOMINEE: TicketNominee = { firstName: '', lastName: '', documentNumber: '', conadisNumber: '' };

@Injectable({ providedIn: 'root' })
export class PurchaseStateService {
    readonly reservation = signal<TicketReservation | null>(null);
    readonly idempotencyKey = signal<string | null>(null);
    private readonly nominations = signal<Record<string, { value: TicketNominee; saved: boolean }>>({});
    readonly attendees = computed<TicketAttendee[]>(() => {
        let ordinal = 0;
        const nominations = this.nominations();
        return this.items().flatMap(item => Array.from({ length: item.cantidad * ticketEntriesPerUnit(item) }, (_, entryIndex) => {
            const key = JSON.stringify([this.eventId(), item.sector.id, item.tarifa.id, entryIndex]);
            return { key, ordinal: ++ordinal, entryIndex, item, value: nominations[key]?.value ?? { ...EMPTY_NOMINEE }, saved: nominations[key]?.saved ?? false };
        }));
    });
    readonly allAttendeesSaved = computed(() => this.attendees().length > 0 && this.attendees().every(attendee => attendee.saved));

    updateNominee(key: string, value: TicketNominee, saved = false): void {
        this.nominations.update(current => ({ ...current, [key]: { value, saved } }));
    }

    readonly orderId = signal<string | null>(null);
    readonly paymentReference = signal<string | null>(null);
    readonly eventId = signal<number | null>(null);
    readonly matchInfo = signal<MatchInfo | null>(null);
    readonly currencyCode = signal('PEN');
    readonly maxTicketsPerOrder = signal(8);
    readonly items = signal<TicketCartItem[]>([]);
    readonly item = computed(() => this.items()[0] ?? DEFAULT_TICKET_ITEM);
    readonly totalTickets = computed(() => this.items().reduce((total, item) => total + item.cantidad * ticketEntriesPerUnit(item), 0));
    readonly totalPackages = computed(() => this.items().reduce((total, item) =>
        total + (ticketEntriesPerUnit(item) > 1 ? item.cantidad : 0), 0));
    readonly hasPreviewItems = computed(() => this.items().some(item => item.isPreview));
    readonly hasPromotionPackages = computed(() => this.items().some(item => item.category === 'children-2x1' || item.category === 'senior-2x1'));
    readonly total = computed(() => this.items().reduce(
        (total, item) => total + ticketLineTotalCents(item),
        0,
    ) / 100);

    setItem(item: TicketCartItem): void {
        this.setItems([item]);
    }

    setItems(items: TicketCartItem[]): void {
        this.reservation.set(null);
        this.idempotencyKey.set(null);
        this.orderId.set(null);
        this.paymentReference.set(null);
        this.items.set(items.filter(item => item.cantidad > 0));
        const keys = new Set(this.attendees().map(attendee => attendee.key));
        this.nominations.update(current => Object.fromEntries(Object.entries(current).filter(([key]) => keys.has(key))));
    }

    setEvent(eventId: number, currencyCode: string, matchInfo?: MatchInfo): void {
        if (this.eventId() !== null && this.eventId() !== eventId) this.clear();
        this.eventId.set(eventId);
        this.currencyCode.set(currencyCode);
        if (matchInfo) this.matchInfo.set(matchInfo);
    }

    clear(): void {
        this.reservation.set(null);
        this.idempotencyKey.set(null);
        this.nominations.set({});
        this.orderId.set(null);
        this.paymentReference.set(null);
        this.eventId.set(null);
        this.matchInfo.set(null);
        this.currencyCode.set('PEN');
        this.maxTicketsPerOrder.set(8);
        this.items.set([]);
    }
}
